import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { CategorySelect } from '@/components/category/category-select'
import { useCategories, useDeleteCategory } from '@/lib/hooks'
import type { Category } from '@/lib/hooks'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchCategoryUsage, reassignTransactionItemsCategory } from '@/lib/api/categories'
import { mutationErrorHandler } from '@/lib/utils'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  category: Category | null
}

function formatLastUsed(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return '今日'
  if (diffDays === 1) return '昨日'
  if (diffDays < 30) return `${diffDays}日前`

  return date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

export function DeleteCategoryDialog({ open, onOpenChange, category }: Props) {
  const queryClient = useQueryClient()
  const deleteCat = useDeleteCategory()
  const { data: categories = [] } = useCategories()

  const [reassignTargetId, setReassignTargetId] = useState<string | null>(null)

  useEffect(() => {
    if (open) setReassignTargetId(null)
  }, [open, category?.id])

  const { data: usage, isLoading } = useQuery({
    queryKey: ['category-usage', category?.id],
    queryFn: () => fetchCategoryUsage(category!.id),
    enabled: open && category !== null,
    staleTime: 0,
  })

  const reassignableCategories = useMemo(() => {
    if (!category) return categories
    return categories.filter(
      (c) => c.id !== category.id && c.parent_id !== category.id
    )
  }, [categories, category])

  const reassignAndDelete = useMutation({
    mutationFn: async ({ id, toId }: { id: string; toId: string | null }) => {
      if (toId) {
        await reassignTransactionItemsCategory(id, toId)
      }
    },
    onError: mutationErrorHandler('引き継ぎに失敗しました'),
  })

  const handleDelete = () => {
    if (!category) return
    const finish = () => {
      deleteCat.mutate(category.id, {
        onSettled: () => onOpenChange(false),
      })
    }

    if (usage && usage.count > 0 && reassignTargetId) {
      reassignAndDelete.mutate(
        { id: category.id, toId: reassignTargetId },
        {
          onSuccess: () => {
            toast.success(`${usage.count}件の取引を引き継ぎました`)
            queryClient.invalidateQueries({ queryKey: ['transactions'] })
            finish()
          },
        }
      )
    } else {
      finish()
    }
  }

  const isPending = deleteCat.isPending || reassignAndDelete.isPending
  const hasUsage = !!usage && usage.count > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>カテゴリを削除</DialogTitle>
          <DialogDescription>
            「{category?.name}」を削除しますか？この操作は元に戻せません。
          </DialogDescription>
        </DialogHeader>

        <div className="text-sm text-muted-foreground space-y-3">
          {isLoading ? (
            <div className="flex items-center gap-2 py-2">
              <Loader2 className="size-4 animate-spin" />
              <span>利用状況を確認中...</span>
            </div>
          ) : hasUsage ? (
            <>
              <div className="space-y-1">
                <p>このカテゴリは <span className="font-medium text-foreground">{usage.count}件</span>の取引で使われています。</p>
                {usage.lastUsedDate && (
                  <p>最近の利用: {formatLastUsed(usage.lastUsedDate)}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <p className="text-foreground">引き継ぎ先カテゴリ</p>
                <CategorySelect
                  categories={reassignableCategories}
                  value={reassignTargetId}
                  onChange={setReassignTargetId}
                  showUncategorized
                />
                <p className="text-xs">
                  {reassignTargetId
                    ? '選択したカテゴリに付け替えてから削除します。'
                    : '引き継ぎ先を選ばない場合、取引はカテゴリ未設定になります。'}
                </p>
              </div>
            </>
          ) : (
            <p>このカテゴリは取引で使われていません。</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
          <Button
            variant={reassignTargetId ? 'default' : 'destructive'}
            onClick={handleDelete}
            disabled={isPending}
          >
            {isPending ? '削除中...' : reassignTargetId ? '引き継いで削除' : '削除'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
